import {NgOptimizedImage} from '@angular/common';
import {ChangeDetectionStrategy, Component, InputSignal, input} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {NgSrcImageErrorDirective} from '../../../../shared/directives/ng-src-image-error.directive';
import {FormCheckboxComponent} from '../../../../shared/elements/form-checkbox/form-checkbox.component';
import {FormLabelComponent} from '../../../../shared/elements/form-label/form-label.component';
import {TranslatePipe} from '../../../../shared/pipes/translate.pipe';
import {FeedbackStructureQuestionFormQuestion, FeedbackStructureQuestionOption} from '../../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {feedbackOptionControlName, previewOptionLabel} from '../../helpers/feedback-structure-preview-control-names';

@Component({
	selector: 'app-feedback-form-element-multi-selection',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormCheckboxComponent, FormLabelComponent, NgOptimizedImage, NgSrcImageErrorDirective, TranslatePipe],
	styles: [`
		.image-radio-tile {
			width: 200px;
		}

		.image-radio {
			border: 1px solid var(--our-border-color);
			cursor: pointer;
			overflow: hidden;
			width: 205px;
			height: 205px;
			max-height: 205px;
		}

		.image-radio,
		.image-radio img,
		.preview-checkbox-text-card {
			border-top-left-radius: var(--our-border-radius);
			border-top-right-radius: var(--our-border-radius);
		}

		.image-radio img {
			width: 100%;
			height: 100%;
			max-height: 205px;
			object-fit: cover;
		}

		.image-radio-selector {
			width: 205px;
			border-bottom-left-radius: var(--our-border-radius);
			border-bottom-right-radius: var(--our-border-radius);
		}

		.preview-checkbox-text-card {
			background-color: var(--bs-body-bg);
			padding: .75rem;
			overflow-wrap: anywhere;
			text-align: center;
		}
	`],
	template: `
		@if (hasAnyOptionImage()) {
			<div class="d-flex flex-wrap justify-content-center gap-4 my-3">
				@for (option of question().options; track option.position) {
					<div class="image-radio-tile d-flex flex-column align-items-center">
						@if (getOptionImagePreview(option); as preview) {
							<div class="image-radio d-flex align-items-center justify-content-center">
								@if(isGifPreview(option.position)) {
									<img
										[ngSrc]="preview"
										[disableOptimizedSrcset]="true"
										class="w-100 h-100 object-fit-cover"
										priority
										width="205"
										height="205"
										[attr.alt]="getLabel(option, ('FEEDBACKANSWER' | translate))"
										appNgSrcImageError
									>
								} @else {
									<img
										[ngSrc]="preview"
										[disableOptimizedSrcset]="false"
										class="w-100 h-100 object-fit-cover"
										priority
										width="300"
										height="300"
										[attr.alt]="getLabel(option, ('FEEDBACKANSWER' | translate))"
										appNgSrcImageError
									>
								}
							</div>
						} @else {
							<div class="image-radio preview-checkbox-text-card d-flex align-items-center justify-content-center">
								<app-form-label
									[label]="getLabel(option, ('FEEDBACKANSWER' | translate))"
									[type]="'content'"
									[class]="'fw-bold text-secondary text-center'"
								></app-form-label>
							</div>
						}

						<div class="d-flex flex-column align-items-center">
							<div class="image-radio-selector d-flex align-items-center justify-content-center pt-2 pb-2 lh-1 border">
								<app-form-checkbox
									[fG]="formGroup()"
									[fCN]="optionControlName(option)"
									[label]="''"
									[labelPosition]="'After'"
									[cssClass]="'pb-1 lh-base'"
									[showErrorState]="false"
									[disabled]="disabled()"
								></app-form-checkbox>
							</div>
						</div>
					</div>
				}
			</div>
		} @else {
			<div class="d-flex flex-column ps-md-3">
				@for (option of question().options; track option.position) {
					<div class="d-flex align-items-center gap-2 mt-2 mb-1">
						<app-form-checkbox
							[fG]="formGroup()"
							[fCN]="optionControlName(option)"
							[label]="getLabel(option, ('FEEDBACKANSWER' | translate))"
							[labelPosition]="'After'"
							[cssClass]="'pb-1 lh-base'"
							[showErrorState]="false"
							[disabled]="disabled()"
						></app-form-checkbox>
					</div>
				}
			</div>
		}
	`,
})
export class FeedbackFormElementMultiSelectionComponent {
	public question: InputSignal<FeedbackStructureQuestionFormQuestion> = input.required();
	public formGroup: InputSignal<FormGroup> = input.required();
	public controlNamePrefix: InputSignal<string> = input('preview');
	public disabled: InputSignal<boolean> = input(false);

	public getLabel(option: FeedbackStructureQuestionOption, fallback: string): string{
		return previewOptionLabel(option, fallback)
	};

	public optionControlName(option: FeedbackStructureQuestionOption): string {
		return feedbackOptionControlName(this.question(), option, this.controlNamePrefix());
	}

	public hasAnyOptionImage(): boolean {
		return this.question().options.some((option) => this.hasOptionImage(option));
	}

	public hasOptionImage(option: FeedbackStructureQuestionOption): boolean {
		return Boolean(this.getOptionImagePreview(option));
	}

	public getOptionImagePreview(option: FeedbackStructureQuestionOption): string | null {
		return option.file?.preview || option.file?.path || null;
	}

	public isGifPreview(postion: number): boolean {
		return /\.gif$/i.test(this.question().options.find((option) => option.position === postion)?.file?.filename ?? '');
	}
}
